import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

/**
 * A single piece of the current user's server-backed "profile data", keyed by a
 * stable string and holding an arbitrary JSON value (GET/PUT /api/me/preferences/
 * <key>/). Unlike a localStorage preference, this follows the user across devices
 * and browsers and is scoped per-user + per-tenant server-side.
 *
 * The generic home for user-scoped UI settings that must persist to the profile:
 * the dashboard layout today; table views / column arrangements next (each is a
 * new key, never a new endpoint). Writes are optimistic (the cache updates
 * immediately) and fire-and-forget to the server.
 */
export interface UserPreferenceHandle<T> {
  value: T
  setValue: (value: T) => void
  /** False until the server value has loaded at least once. */
  isLoaded: boolean
}

export function useUserPreference<T>(key: string, fallback: T): UserPreferenceHandle<T> {
  const qc = useQueryClient()
  const queryKey = ['user-preference', key] as const

  const query = useQuery<T>({
    queryKey,
    queryFn: async () => {
      const res = await api.get(`/me/preferences/${encodeURIComponent(key)}/`)
      const value = (res.data as { value: T | null } | undefined)?.value
      return (value ?? fallback) as T
    },
    // Profile data is small and rarely changes out from under the user; the
    // optimistic cache update keeps it fresh within the session.
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (value: T) =>
      api.put(`/me/preferences/${encodeURIComponent(key)}/`, { value }),
    // One write at a time per key, so quick toggles reach the server in order.
    scope: { id: `user-preference:${key}` },
  })

  const setValue = useCallback(
    (value: T) => {
      // Optimistic: update the cache immediately so the UI never waits on the
      // round-trip, then persist to the profile. A read still in flight would
      // land with the old value, so drop it first.
      void qc.cancelQueries({ queryKey })
      qc.setQueryData(queryKey, value)
      mutation.mutate(value)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  )

  return {
    value: query.data ?? fallback,
    setValue,
    isLoaded: !query.isPending,
  }
}
